import React, { memo, useCallback } from 'react';

import { Product } from 'types/graphql-overrides';

import { useProductCardData } from './use-product-card-data';
import { CardView } from './views/card-view';

type CardProps = {
  product: Product;
  productIndex: number;
  hideSponsoredTag?: boolean;
  onClick: (product: Product, index: number) => void;
  onQuickAddClick: (product: Product, index: number) => void;
  onView?: (product: Product, index: number) => void;
  'data-testid'?: string;
};

/**
 * Container for the grid product card.
 * Owns data fetching and action binding; delegates rendering to CardView.
 */
const Card = ({
  product,
  productIndex,
  hideSponsoredTag = false,
  onClick,
  onQuickAddClick,
  onView,
  'data-testid': testId,
}: CardProps): JSX.Element => {
  const data = useProductCardData({
    product,
    productIndex,
    onView,
    viewportThreshold: 0.5,
  });

  const handleLinkClick = useCallback((): void => {
    onClick(product, productIndex);
  }, [onClick, product, productIndex]);

  const handleAddToCart = useCallback((): void => {
    onQuickAddClick(product, productIndex);
  }, [onQuickAddClick, product, productIndex]);

  return (
    <CardView
      product={data.product}
      image={data.image}
      badges={data.badges}
      pricing={data.pricing}
      layout={{ ...data.layout, hideSponsoredTag }}
      onLinkClick={handleLinkClick}
      onAddToCart={handleAddToCart}
      containerRef={data.containerRef}
      productIndex={data.productIndex}
      data-testid={testId}
    />
  );
};

export default memo(Card);
